<template>
  <component :is="wrapperTag" class="d-flex flex-column gap-2">
    <title-node v-bind="computedTitle" />
    <keep-alive>
      <component
        :is="question"
        v-model="modelValue"
        :rules="computedRules"
        :disabled="disabled"
        v-bind="computedQuestionData"
      />
    </keep-alive>
  </component>
</template>

<script>
import { Component, ComputedOptions } from 'vue'
import { Computed, ModelValueProp, PropsDefinition } from '@/_typedefs'
import SimpleTextInputComponentVue from './TextInput/SimpleTextInput.component.vue'
import MaskedTextInputComponentVue from './TextInput/MaskedTextInput.component.vue'
import LongTextInputComponentVue from './TextInput/LongTextInput.component.vue'
import DateInputComponentVue from './TextInput/DateInput.component.vue'
import RadioGroupComponentVue from './Radio/RadioGroup.component.vue'
import CheckboxGroupVue from './Checkbox/CheckboxGroup.vue'
import CheckboxDetailGroupVue from './CheckboxDetail/CheckboxDetailGroup.vue'
import SelectComponentVue from './Select/Select.component.vue'
import AutocompleteComponentVue from './Autocomplete/Autocomplete.vue'
import TitleNode, { TitleProps } from '../../FormNodes/Title/TitleNode.vue'
import { createNamespacedHelpers } from 'vuex'
// import { basicMap } from '../../helper/lib/componentsMapping/questionComponents'

const {
  mapActions: newAttendanceFormActions,
  mapMutations: newAttendanceFormMutations,
  mapGetters: newAttendanceFormGetters,
} = createNamespacedHelpers('newAttendanceForm')

/** @typedef {any} ModelValue */
/**
 * @typedef {Object} BasicQuestion
 * @property {TitleProps} titleProps
 * @property {string} title
 * @property {string} kind
 * @property {boolean} required
 * @property {boolean} isComputed
 * @property {boolean} disabled
 * @property {object} questionData
 */
/** @typedef {BasicQuestion & ModelValueProp<ModelValue>} Props */

export default {
  name: 'BasicQuestionComponent',
  components: { TitleNode },
  /** @type {PropsDefinition<Props>} */
  props: {
    value: {
      type: [Object, String, Number, Boolean, Array],
      validator: (v) => ['object', 'string', 'number', 'boolean'].includes(typeof v) || v === null,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    titleProps: {
      type: Object,
      default: null,
    },
    kind: {
      type: String,
      required: true,
      validator: (v) =>
        [
          'text',
          'masked-text',
          'textarea',
          'date',
          'radio',
          'checkbox',
          'checkbox-group',
          'checkbox-detail-group',
          'select',
          'autocomplete',
        ].includes(v),
    },
    required: {
      type: Boolean,
      default: false,
    },
    questionData: {
      type: Object,
    },
    isComputed: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...newAttendanceFormGetters(['answers', 'questions']),
    /** @type {Computed<Component | null>} */
    question() {
      const basicMap = {
        text: SimpleTextInputComponentVue,
        'masked-text': MaskedTextInputComponentVue,
        textarea: LongTextInputComponentVue,
        date: DateInputComponentVue,
        radio: RadioGroupComponentVue,
        checkbox: null,
        'checkbox-group': CheckboxGroupVue,
        'checkbox-detail-group': CheckboxDetailGroupVue,
        select: SelectComponentVue,
        autocomplete: AutocompleteComponentVue,
      }
      return basicMap[this.kind] ?? null
    },
    /** @type {Computed<'div' | 'label'>} */
    wrapperTag() {
      const tagMap = {
        text: 'label',
        textarea: 'label',
        date: 'label',
      }

      return tagMap[this.type] ?? 'div'
    },
    /** @type {Computed<object>} */
    computedTitle() {
      return {
        text: this.required ? `* ${this.title}` : this.title,
        small: true,
        ...this.titleProps,
      }
    },
    /** @type {Computed<object>} */
    computedQuestionData() {
      return {
        ...this.questionData,
        ...(this.isComputed ? { disabled: true } : {}),
      }
    },
    /** @type {Computed<Array<(v: any) => boolean | string>>} */
    computedRules() {
      const rules = []

      if (this.required) {
        rules.push(this.kind === 'radio' ? this.$validation.radioRequired() : this.$validation.required())
      }

      return rules
    },
    /** @type {ComputedOptions<any>} */
    modelValue: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
  },
}
</script>

<style></style>
